package top.wcpe.allincore.ui.runtime.canvas

import top.wcpe.allincore.dsl.ir.canvas.DrawBlob
import top.wcpe.allincore.dsl.ir.canvas.DrawOpcodes
import top.wcpe.allincore.dsl.ir.canvas.Operand
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext

/**
 * DrawBlob 解释器。
 *
 * 顺序执行 DrawBlob 中的 4 种基础图元指令（RECT / CIRCLE / DOT / LINE），
 * 输出 [DrawCommand] 列表供渲染层消费。
 *
 * M11.2 升级：
 * - 操作数支持 `LITERAL`（M11.1 行为）和 `EXPR`（ExprBlob）两种 kind。
 * - [execute] 注入内置变量
 *   `t`（归一化进度 0..1）、`now`（当前 ms）、
 *   `width` / `height`（canvas 像素尺寸）到 EvalContext locals 域，
 *   供 EXPR 操作数引用。
 *
 * M11.3 升级：支持控制流 opcode `VAL_ASSIGN / FOR_BEGIN / IF_BEGIN`，
 * 维护固定大小的 `double[16]` locals 槽数组供 val 与 for 变量使用；
 * 单帧累计图元指令数超过 [MAX_INSTRUCTION_COUNT] 时截断并 warn。
 * 控制流变量通过名为 `$slotN` 的 local 别名注入 EvalContext，供 EXPR 操作数引用。
 *
 * 错误处理：
 * - 构造期通过 [BlobScanner] 一次性扫描字节流；损坏立即抛
 *   [CanvasExecutionException]（[isBlobBroken] 返回 true）。
 * - 执行期单条顶层指令求值失败时，把错误信息累计起来在帧末统一限频 warn，
 *   游标跳到该指令字节末尾继续下一条；FOR/IF 子块的内部异常仍向上抛出
 *   并整块跳过（子块内做局部隔离会破坏分支语义）。
 * - 同一 canvas + 同类错误（按 opcode 聚合）按 `5s` 限频；
 *   上下文信息附 canvasId / opcode / byteOffset。
 */
@Suppress("TooManyFunctions")
class DrawInterpreter(drawBlob: ByteArray?) {
    private val drawBlob: ByteArray

    init {
        require(drawBlob != null) { "drawBlob 不能为 null" }
        this.drawBlob = drawBlob
    }

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.canvas.DrawInterpreter")

        /** 单帧图元指令硬上限（RFC §5.2 防御性截断阈值）。 */
        const val MAX_INSTRUCTION_COUNT: Int = 4096

        /** 本解释器为 slot 变量分配的 EvalContext local 名称前缀。 */
        const val SLOT_VAR_PREFIX: String = "${'$'}slot"

        /** 同一类错误日志的限频窗口（毫秒）。 */
        private const val LOG_THROTTLE_WINDOW_MS: Long = 5000L

        /**
         * 全局错误日志限频器。key 形如 `canvasId#opcode`。
         *
         * key 数量受限于"活跃 canvas 数 × 出错 opcode 种类"，正常运行下规模数十到数百，
         * 每条 entry < 64 字节，无需 LRU。若未来出现"长生命周期但反复创建/销毁 canvas id"
         * 的场景（如频繁打开/关闭弹窗导致 id 不断变化），再考虑改为 LRU。
         */
        private val LOG_THROTTLE: LogThrottle = LogThrottle(LOG_THROTTLE_WINDOW_MS)

        /** 字节掩码：opcode 以单字节无符号形式打印。 */
        private const val BYTE_MASK: Int = 0xFF

        /** 图元 opcode 集合，用于快速判断是否为图元指令。 */
        private val PRIMITIVE_OPCODES: Set<Byte> = setOf(
            DrawBlob.OP_RECT,
            DrawBlob.OP_CIRCLE,
            DrawBlob.OP_DOT,
            DrawBlob.OP_LINE,
            DrawBlob.OP_ARC,
            DrawBlob.OP_RING,
            DrawBlob.OP_ROUND_RECT,
            DrawBlob.OP_TEXT,
            DrawBlob.OP_DRAW_IMAGE,
            DrawBlob.OP_POLYGON,
            DrawBlob.OP_PATH,
            DrawBlob.OP_BEZIER_QUAD,
            DrawBlob.OP_BEZIER_CUBIC,
            DrawBlob.OP_PATH_BUILD,
            DrawBlob.OP_BITMAP,
        )
    }

    /** 顶层指令的字节末尾跳表；空 blob 或损坏 blob 为长度 0 的空数组。 */
    private val topLevelInstructionEnds: IntArray

    /** 构造期扫描发现的损坏信息；非 null 时 [execute] 直接返回空列表。 */
    private val blobBroken: CanvasExecutionException?

    /** draw 中是否含 EXPR 操作数；用于禁止静态命令缓存误命中动态内容。 */
    private val dynamicExpressions: Boolean

    /** 渲染层注入的 canvas id，便于异常上下文与日志限频键值。 */
    var canvasId: String? = null
        private set

    /** 注入 canvas id，便于异常上下文与日志限频键值；幂等。 */
    fun canvasId(id: String) {
        this.canvasId = id
    }

    /** 返回构造期是否检测到字节流损坏。 */
    fun isBlobBroken(): Boolean = blobBroken != null

    init {
        val result = try {
            val ends = BlobScanner.scan(this.drawBlob, null)
            Pair(ends, null)
        } catch (e: CanvasExecutionException) {
            // 构造期 warn 一次；后续 execute 不再重复日志
            LOGGER.warn("[ui] DrawBlob 字节流损坏，canvas 将渲染为空：${e.message}")
            Pair(IntArray(0), e)
        }
        this.topLevelInstructionEnds = result.first
        this.blobBroken = result.second
        this.dynamicExpressions = result.second == null && detectDynamicExpressions()
    }

    /** draw 中存在 EXPR 操作数时返回 true。 */
    fun hasDynamicExpressions(): Boolean = dynamicExpressions

    private fun detectDynamicExpressions(): Boolean = runCatching {
        DrawBlob.decode(drawBlob).any { instruction ->
            instruction.operandsArray.any { operand -> operand is Operand.Expr }
        }
    }.getOrDefault(true)

    /**
     * 解释执行；EXPR 操作数在内层 EvalContext 上求值（注入 t/now/width/height）。
     *
     * 全部参数都提供默认值，调用方可按需使用命名参数：
     * - `execute()`：使用全部默认值
     * - `execute(nowMs = ..., width = ..., height = ..., parent = ...)`：无鼠标坐标的简化调用
     * - `execute(t = ..., nowMs = ..., width = ..., height = ..., parent = ...)`：带进度但无鼠标
     * - 完整参数调用：适用于需要鼠标坐标的场景
     *
     * @param t         归一化进度 0..1
     * @param nowMs     当前时间（毫秒）
     * @param width     canvas 像素宽
     * @param height    canvas 像素高
     * @param mouseX    canvas 局部坐标系下的鼠标 X
     * @param mouseY    canvas 局部坐标系下的鼠标 Y
     * @param mouseInside 鼠标是否当前位于本 canvas 命中盒内
     * @param parent    外层 EvalContext；可为 null（仅含内置变量的最小上下文）
     */
    fun execute(
        t: Double,
        nowMs: Long,
        width: Int,
        height: Int,
        mouseX: Double,
        mouseY: Double,
        mouseInside: Boolean,
        parent: EvalContext?,
    ): MutableList<DrawCommand> {
        if (blobBroken != null || drawBlob.isEmpty()) {
            return mutableListOf()
        }
        val locals = DoubleArray(DrawBlob.LOCAL_SLOT_COUNT)
        val ctx = CanvasExprEvaluator.buildCanvasCtx(parent, t, nowMs, width, height, mouseX, mouseY, mouseInside)
        val result = mutableListOf<DrawCommand>()
        val buf = BlobReader(drawBlob)
        buf.short // u16 count 头：仅推进游标
        // 顶层用跳表驱动以便对单条指令做隔离恢复；FOR/IF 子块仍走 execRange 顺序解释。
        val state = ExecState()
        executeTopLevel(buf, locals, ctx, result, state, nowMs)
        if (state.truncated) {
            LOGGER.warn("[ui] DrawInterpreter 单帧指令数超过上限 $MAX_INSTRUCTION_COUNT，已截断")
        }
        return result
    }

    /** 兼容入口：不带时间，t=0、now=0。仅作占位/测试用。 */
    fun execute(): MutableList<DrawCommand> = execute(0.0, 0L, 0, 0, 0.0, 0.0, false, null)

    /** 兼容入口：4 参数版本。 */
    fun execute(nowMs: Long, width: Int, height: Int, parent: EvalContext?): MutableList<DrawCommand> =
        execute(0.0, nowMs, width, height, 0.0, 0.0, false, parent)

    /** 兼容入口：5 参数版本（parent 在第 5 位）。 */
    fun execute(t: Double, nowMs: Long, width: Int, height: Int, parent: EvalContext?): MutableList<DrawCommand> =
        execute(t, nowMs, width, height, 0.0, 0.0, false, parent)

    /** 执行状态：共享的已发射指令计数与截断标记。 */
    private class ExecState {
        var instructionCounter: Int = 0
        var truncated: Boolean = false
    }

    /**
     * 顶层指令循环：用 [topLevelInstructionEnds] 跳表逐条执行。
     * 单条求值失败时限频 warn，并 seek 到该指令末端继续下一条。
     */
    private fun executeTopLevel(
        buf: BlobReader,
        locals: DoubleArray,
        ctx: EvalContext,
        out: MutableList<DrawCommand>,
        state: ExecState,
        nowMs: Long,
    ) {
        for (instEnd in topLevelInstructionEnds) {
            if (state.truncated) return
            val instStart = buf.position()
            val op = buf.get()
            try {
                dispatch(op, buf, instEnd, locals, ctx, out, state, canvasId, nowMs)
            } catch (e: CanvasExecutionException) {
                // 限频日志输出
                val key = "${e.canvasId}#${op.toInt() and BYTE_MASK}"
                if (LOG_THROTTLE.allow(key, nowMs)) {
                    LOGGER.warn("[ui] canvas draw 指令执行失败（已隔离，${LOG_THROTTLE_WINDOW_MS}ms 内同类错误不再打印）：${e.message}", e)
                }
                buf.position(instEnd)
            } catch (e: RuntimeException) {
                val wrapped = CanvasExecutionException(
                    canvasId,
                    op.toInt() and BYTE_MASK,
                    instStart,
                    "draw 指令执行失败: ${e.message}",
                    e,
                )
                // 限频日志输出
                val key = "${wrapped.canvasId}#${op.toInt() and BYTE_MASK}"
                if (LOG_THROTTLE.allow(key, nowMs)) {
                    LOGGER.warn(
                        "[ui] canvas draw 指令执行失败（已隔离，${LOG_THROTTLE_WINDOW_MS}ms 内同类错误不再打印）：${wrapped.message}",
                        wrapped,
                    )
                }
                buf.position(instEnd)
            }
        }
    }

    /**
     * 在 [buf.position(), endPos) 范围内顺序解释执行 FOR/IF 子块。
     * 子块内异常向上抛，由外层顶层循环负责隔离与日志。
     */
    private fun execRange(
        buf: BlobReader,
        endPos: Int,
        locals: DoubleArray,
        ctx: EvalContext,
        out: MutableList<DrawCommand>,
        state: ExecState,
        canvasId: String?,
        nowMs: Long,
    ) {
        while (buf.position() < endPos) {
            if (state.truncated) {
                buf.position(endPos)
                return
            }
            val op = buf.get()
            dispatch(op, buf, endPos, locals, ctx, out, state, canvasId, nowMs)
        }
    }

    /** 单条 opcode 的分派；失败一律抛 [RuntimeException]/[CanvasExecutionException]。 */
    private fun dispatch(
        op: Byte,
        buf: BlobReader,
        endPos: Int,
        locals: DoubleArray,
        ctx: EvalContext,
        out: MutableList<DrawCommand>,
        state: ExecState,
        canvasId: String?,
        nowMs: Long,
    ) {
        // 图元指令统一处理
        if (op in PRIMITIVE_OPCODES) {
            if (state.instructionCounter >= MAX_INSTRUCTION_COUNT) {
                state.truncated = true
                buf.position(endPos)
                return
            }
            out.add(readAndEvalPrimitive(op, buf, ctx))
            state.instructionCounter++
            return
        }

        // 控制流 opcode 处理
        when (op) {
            DrawBlob.OP_VAL_ASSIGN -> {
                val slot = buf.get().toInt() and BYTE_MASK
                val value = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
                locals[slot] = value
                setSlot(ctx, slot, value)
            }
            DrawBlob.OP_FOR_BEGIN,
            DrawBlob.OP_IF_BEGIN,
            -> handleFlowBegin(op, buf, locals, ctx, out, state, endPos, canvasId, nowMs)
            // 正常情况下 FOR/IF_BEGIN 分支已吃掉对应 END；此处兜底忽略。
            DrawBlob.OP_FOR_END, DrawBlob.OP_IF_END -> {}
            else -> throw IllegalStateException("未知 draw opcode: 0x${(op.toInt() and BYTE_MASK).toString(16)}")
        }
    }

    /**
     * 处理 FOR_BEGIN 或 IF_BEGIN：根据 opcode 分发到对应的控制流逻辑。
     *
     * FOR_BEGIN 读取 slot/iter/bodyLen，按迭代次数重复解释循环体；
     * IF_BEGIN 先求条件 truthy，再执行 then 段或 else 段。
     *
     * **TODO（已知小瑕疵）**：FOR 块整体作为顶层指令执行；若块内某次迭代抛错，
     * 顶层捕获后该 FOR_BEGIN 整块跳过，但 `$slotN` 已在
     * [setSlot] 阶段声明并写入 EvalContext，残留到后续顶层指令。
     * 实际影响很小：draw 块语义上 slot 作用域应限定于声明它的 for/val，
     * 后续顶层指令一般不引用循环变量；就算引用也只是读到上次值，不会崩溃。
     * 修复需要在每条顶层指令调度前传入 EvalContext 副本，会显著影响热路径性能，
     * 收益不成比例，故暂不修。
     */
    private fun handleFlowBegin(
        op: Byte,
        buf: BlobReader,
        locals: DoubleArray,
        ctx: EvalContext,
        out: MutableList<DrawCommand>,
        state: ExecState,
        endPos: Int,
        canvasId: String?,
        nowMs: Long,
    ) {
        when (op) {
            DrawBlob.OP_FOR_BEGIN -> {
                val slot = buf.get().toInt() and BYTE_MASK
                val iter = buf.short.toInt() and 0xFFFF
                val bodyLen = buf.short.toInt() and 0xFFFF
                val bodyStart = buf.position()
                val bodyEnd = bodyStart + bodyLen
                // 进入循环前先声明一次 slot 变量，循环体内仅做 setVar 更新，
                // 避免每次迭代都重复走 hasVar 检查的热路径。
                if (iter > 0) {
                    setSlot(ctx, slot, 0.0)
                }
                for (i in 0 until iter) {
                    if (state.truncated) break
                    locals[slot] = i.toDouble()
                    setSlot(ctx, slot, i.toDouble())
                    buf.position(bodyStart)
                    execRange(buf, bodyEnd, locals, ctx, out, state, canvasId, nowMs)
                }
                buf.position(bodyEnd)
                // 跳过紧随其后的 FOR_END 标记（若存在）
                if (buf.position() < endPos && buf.get(buf.position()) == DrawBlob.OP_FOR_END) {
                    buf.get()
                }
            }
            DrawBlob.OP_IF_BEGIN -> {
                val cond = CanvasExprEvaluator.readOperand(buf)
                val thenLen = buf.short.toInt() and 0xFFFF
                val elseLen = buf.short.toInt() and 0xFFFF
                val thenStart = buf.position()
                val thenEnd = thenStart + thenLen
                val elseEnd = thenEnd + elseLen
                val truthy = CanvasExprEvaluator.isTruthy(cond, ctx)
                if (truthy) {
                    execRange(buf, thenEnd, locals, ctx, out, state, canvasId, nowMs)
                } else {
                    buf.position(thenEnd)
                    execRange(buf, elseEnd, locals, ctx, out, state, canvasId, nowMs)
                }
                buf.position(elseEnd)
                if (buf.position() < endPos && buf.get(buf.position()) == DrawBlob.OP_IF_END) {
                    buf.get()
                }
            }
        }
    }

    /** 将 slot 值以 $slotN 名义写入 EvalContext；首次写入用 declareVar，后续用 setVar。 */
    private fun setSlot(ctx: EvalContext, slot: Int, value: Double) {
        val name = SLOT_VAR_PREFIX + slot
        if (ctx.hasVar(name)) {
            ctx.setVar(name, value)
        } else {
            ctx.declareVar(name, value)
        }
    }

    /** 读取一条图元指令的操作数并翻译为 DrawCommand。 */
    private fun readAndEvalPrimitive(op: Byte, buf: BlobReader, ctx: EvalContext): DrawCommand = when (op) {
        DrawBlob.OP_RECT,
        DrawBlob.OP_CIRCLE,
        DrawBlob.OP_DOT,
        DrawBlob.OP_LINE,
        DrawBlob.OP_ARC,
        DrawBlob.OP_RING,
        -> readShapePrimitive(op, buf, ctx)
        DrawBlob.OP_TEXT,
        DrawBlob.OP_DRAW_IMAGE,
        DrawBlob.OP_BITMAP,
        -> readTextPrimitive(op, buf, ctx)
        DrawBlob.OP_ROUND_RECT,
        DrawBlob.OP_POLYGON,
        DrawBlob.OP_PATH,
        DrawBlob.OP_BEZIER_QUAD,
        DrawBlob.OP_BEZIER_CUBIC,
        -> readBezierPrimitive(op, buf, ctx)
        DrawBlob.OP_PATH_BUILD -> readPathBuild(buf, ctx)
        else -> throw IllegalStateException("不是图元 opcode: 0x${(op.toInt() and BYTE_MASK).toString(16)}")
    }

    /** 读取简单图元（RECT/CIRCLE/DOT/LINE/ARC/RING）的操作数。 */
    @Suppress("LongMethod")
    private fun readShapePrimitive(op: Byte, buf: BlobReader, ctx: EvalContext): DrawCommand = when (op) {
        DrawBlob.OP_RECT -> {
            val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val h = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Rect(x, y, w, h, color)
        }
        DrawBlob.OP_CIRCLE -> {
            val cx = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val cy = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val r = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Circle(cx, cy, r, color)
        }
        DrawBlob.OP_DOT -> {
            val cx = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val cy = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val r = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Dot(cx, cy, r, color)
        }
        DrawBlob.OP_LINE -> {
            val x1 = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y1 = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val x2 = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y2 = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Line(x1, y1, x2, y2, w, color)
        }
        DrawBlob.OP_ARC -> {
            val cx = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val cy = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val r = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val start = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val end = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Arc(cx, cy, r, start, end, color)
        }
        DrawBlob.OP_RING -> {
            val cx = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val cy = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val r = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Ring(cx, cy, r, w, color)
        }
        else -> throw IllegalStateException("不是简单图元 opcode: 0x${(op.toInt() and BYTE_MASK).toString(16)}")
    }

    /** 读取文本/图片/位图图元（TEXT/DRAW_IMAGE/BITMAP）的操作数。 */
    private fun readTextPrimitive(op: Byte, buf: BlobReader, ctx: EvalContext): DrawCommand = when (op) {
        DrawBlob.OP_TEXT -> {
            val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val size = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            val content = CanvasExprEvaluator.readStr(buf)
            DrawCommand.Text(x, y, size, color, content)
        }
        DrawBlob.OP_DRAW_IMAGE -> {
            val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val h = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val resourceId = CanvasExprEvaluator.readStr(buf)
            DrawCommand.Image(x, y, w, h, resourceId)
        }
        DrawBlob.OP_BITMAP -> {
            // v4 B.5：data(OPERAND_BYTES), srcW, srcH, paletteId, dstX, dstY, dstW, dstH
            val data = CanvasExprEvaluator.readBytes(buf)
            val srcW = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toInt()
            val srcH = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toInt()
            val paletteId = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toInt()
            val dstX = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val dstY = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val dstW = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val dstH = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            DrawCommand.Bitmap(data, srcW, srcH, paletteId, dstX, dstY, dstW, dstH)
        }
        else -> throw IllegalStateException(
            "不是文本/图片/位图图元 opcode: 0x${(op.toInt() and BYTE_MASK).toString(16)}",
        )
    }

    /** 读取复杂图元（ROUND_RECT/POLYGON/PATH/BEZIER_QUAD/BEZIER_CUBIC）的操作数。 */
    @Suppress("LongMethod")
    private fun readBezierPrimitive(op: Byte, buf: BlobReader, ctx: EvalContext): DrawCommand = when (op) {
        DrawBlob.OP_ROUND_RECT -> {
            val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val h = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val r = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.RoundRect(x, y, w, h, r, color)
        }
        DrawBlob.OP_POLYGON -> {
            // u8 vertexCount + (x,y)×N + color
            val vertexCount = buf.get().toInt() and BYTE_MASK
            val vertices = DoubleArray(vertexCount * 2)
            for (i in vertices.indices) {
                vertices[i] = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            }
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.Polygon(vertices, color)
        }
        DrawBlob.OP_PATH -> {
            // u8 vertexCount + (x,y)×N + width + color + u8 closed
            val vertexCount = buf.get().toInt() and BYTE_MASK
            val vertices = DoubleArray(vertexCount * 2)
            for (i in vertices.indices) {
                vertices[i] = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            }
            val width = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            val closed = (buf.get().toInt() and BYTE_MASK) != 0
            DrawCommand.Path(vertices, width, color, closed)
        }
        DrawBlob.OP_BEZIER_QUAD -> {
            // p0x, p0y, c1x, c1y, p1x, p1y, width, color
            val p0x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p0y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c1x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c1y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p1x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p1y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.BezierQuad(p0x, p0y, c1x, c1y, p1x, p1y, w, color)
        }
        DrawBlob.OP_BEZIER_CUBIC -> {
            // p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, width, color
            val p0x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p0y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c1x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c1y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c2x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val c2y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p1x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val p1y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val w = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
            val color = CanvasExprEvaluator.toColor(
                CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx),
            )
            DrawCommand.BezierCubic(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, w, color)
        }
        else -> throw IllegalStateException("不是复杂图元 opcode: 0x${(op.toInt() and BYTE_MASK).toString(16)}")
    }

    /** 读取 PATH_BUILD 指令的操作数。 */
    private fun readPathBuild(buf: BlobReader, ctx: EvalContext): DrawCommand {
        // u8 cmdCount + cmdCount×(u8 cmdKind + N×Operand) + u8 modeByte + width + color
        val cmdCount = buf.get().toInt() and BYTE_MASK
        val commands = arrayOfNulls<top.wcpe.allincore.ui.runtime.render.model.PathCommand>(cmdCount)
        for (i in 0 until cmdCount) {
            val k = buf.get()
            commands[i] = when (k) {
                DrawOpcodes.PATH_NODE_MOVE_TO -> {
                    val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    top.wcpe.allincore.ui.runtime.render.model.PathCommand.MoveTo(x, y)
                }
                DrawOpcodes.PATH_NODE_LINE_TO -> {
                    val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    top.wcpe.allincore.ui.runtime.render.model.PathCommand.LineTo(x, y)
                }
                DrawOpcodes.PATH_NODE_QUAD_TO -> {
                    val cx = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val cy = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    top.wcpe.allincore.ui.runtime.render.model.PathCommand.QuadTo(cx, cy, x, y)
                }
                DrawOpcodes.PATH_NODE_CUBIC_TO -> {
                    val c1x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val c1y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val c2x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val c2y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val x = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    val y = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx).toFloat()
                    top.wcpe.allincore.ui.runtime.render.model.PathCommand.CubicTo(c1x, c1y, c2x, c2y, x, y)
                }
                DrawOpcodes.PATH_NODE_CLOSE -> top.wcpe.allincore.ui.runtime.render.model.PathCommand.Close()
                else -> throw IllegalStateException(
                    "未知 PATH_BUILD 节点种类: 0x${(k.toInt() and BYTE_MASK).toString(16)}",
                )
            }
        }
        val mode = buf.get().toInt() and BYTE_MASK
        val width = CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx)
        val color = CanvasExprEvaluator.toColor(CanvasExprEvaluator.evalNum(CanvasExprEvaluator.readOperand(buf), ctx))
        @Suppress("UNCHECKED_CAST")
        return DrawCommand.PathBuild(
            commands as Array<top.wcpe.allincore.ui.runtime.render.model.PathCommand>,
            mode,
            width,
            color,
        )
    }
}
